(() => {
  const initializedRoots = new WeakSet();
  const observedRoots = new WeakSet();
  let cardSyncScheduled = false;

  function eventDetail(root, extra = {}) {
    return {
      product_id: root.dataset.productId || '',
      product_handle: root.dataset.productHandle || '',
      component_version: Number(root.dataset.choiceSupportVersion) || 1,
      context: root.dataset.choiceSupportContext || '',
      ...extra,
    };
  }

  function emit(eventName, root, extra = {}) {
    const detail = eventDetail(root, extra);

    document.dispatchEvent(new CustomEvent(`ssc:${eventName}`, { detail }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `ssc_${eventName}`, ...detail });

    try {
      window.Shopify?.analytics?.publish?.(`ssc_${eventName}`, detail);
    } catch (error) {
      console.warn('Product choice-support analytics publish failed.', error);
    }
  }

  function matchingSelect(root, group) {
    const optionPositionValue = group.dataset.choiceOptionPosition || '';
    if (!/^[0-2]$/.test(optionPositionValue)) return null;
    const optionPosition = Number(optionPositionValue);

    const select = root
      .closest('product-form')
      ?.querySelector(`.native-option-select[data-option-position="${optionPosition}"]`);

    if (!select || select.dataset.optionName !== group.dataset.choiceOptionName) return null;
    return select;
  }

  function syncCards(root) {
    root.querySelectorAll('[data-choice-support-group]').forEach(group => {
      const select = matchingSelect(root, group);
      if (!select) return;

      group.querySelectorAll('[data-choice-card]').forEach(card => {
        const selected = card.dataset.choiceOptionValue === select.value;
        const action = card.querySelector('[data-choice-support-action]');
        card.classList.toggle('is-selected', selected);
        if (action) {
          action.setAttribute('aria-pressed', String(selected));
          action.textContent = selected
            ? 'Selected'
            : (action.dataset.choiceActionLabel || 'Use this option');
        }
      });
    });
  }

  function syncAllCards() {
    document.querySelectorAll('[data-product-choice-support]').forEach(syncCards);
  }

  function scheduleCardSync() {
    if (cardSyncScheduled) return;
    cardSyncScheduled = true;

    const synchronize = () => {
      cardSyncScheduled = false;
      syncAllCards();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(synchronize);
    } else {
      synchronize();
    }
  }

  function applyChoice(event, root) {
    const action = event.target.closest('[data-choice-support-action]');
    if (!action || !root.contains(action)) return;

    const group = action.closest('[data-choice-support-group]');
    const select = group ? matchingSelect(root, group) : null;
    const optionValue = action.dataset.choiceOptionValue || '';
    const priorOptionValue = select?.value || '';
    const hasExactOption = Boolean(
      select && Array.from(select.options).some(option => option.value === optionValue),
    );

    if (!hasExactOption) {
      emit('product_choice_support_error', root, {
        group_key: group?.dataset.choiceGroupKey || '',
        option_name: group?.dataset.choiceOptionName || '',
        option_value: optionValue,
        reason: select ? 'configured_option_not_found' : 'selector_not_found',
      });
      return;
    }

    select.value = optionValue;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncCards(root);
    select.focus({ preventScroll: true });

    emit('product_choice_support_select', root, {
      group_key: group.dataset.choiceGroupKey || '',
      option_name: group.dataset.choiceOptionName || '',
      option_value: optionValue,
      prior_option_value: priorOptionValue,
      selection_applied: select.value === optionValue,
    });
  }

  function observeImpression(root) {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    const markViewed = () => {
      if (root.dataset.choiceSupportViewed === 'true') return;
      root.dataset.choiceSupportViewed = 'true';
      emit('product_choice_support_view', root, {
        group_count: root.querySelectorAll('[data-choice-support-group]').length,
      });
    };

    if (!('IntersectionObserver' in window)) {
      if (root.offsetParent !== null) markViewed();
      return;
    }

    const rootHeight = root.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const threshold = rootHeight > 0 && viewportHeight > 0
      ? Math.min(0.5, (viewportHeight * 0.5) / rootHeight)
      : 0.5;

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= threshold)) {
        markViewed();
        observer.disconnect();
      }
    }, { threshold });

    observer.observe(root);
  }

  function syncDisclosureLayout(root) {
    root.querySelectorAll('[data-choice-support-details]').forEach(details => {
      details.open = false;
    });
  }

  function purchaseForm(root) {
    return root.closest('form');
  }

  function exactSelectionIsReady(root, form) {
    const groups = Array.from(root.querySelectorAll('[data-choice-support-group]'));
    if (!form || groups.length === 0) return false;

    const selectionsAreExact = groups.every(group => {
      const select = matchingSelect(root, group);
      if (!select?.value) return false;

      return Array.from(group.querySelectorAll('[data-choice-card]')).some(
        card => card.dataset.choiceOptionValue === select.value,
      );
    });
    const variantInput = form.querySelector('[name="id"]');

    return Boolean(selectionsAreExact && variantInput?.value);
  }

  function unlockPurchaseForm(root) {
    const form = purchaseForm(root);
    const lock = form?.querySelector('[data-choice-support-form-lock]');
    if (!form || !lock || lock.dataset.choiceSupportReady === 'true') return;

    const firstGroup = root.querySelector('[data-choice-support-group]');
    const firstSelect = firstGroup ? matchingSelect(root, firstGroup) : null;

    // Force the existing native variant resolver to synchronize the hidden ID
    // while the server-rendered form boundary is still locked.
    firstSelect?.dispatchEvent(new Event('change', { bubbles: true }));

    if (!exactSelectionIsReady(root, form)) {
      emit('product_choice_support_error', root, { reason: 'purchase_lock_not_ready' });
      return;
    }

    lock.dataset.choiceSupportReady = 'true';
    form.querySelector('[name="id"]').disabled = false;

    // Re-run native availability/configuration logic after the lock is clear;
    // that existing logic remains authoritative for sold-out and required-field
    // button states.
    firstSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initializeRoot(root) {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    root.addEventListener('click', event => applyChoice(event, root));
    root.closest('product-form')?.querySelectorAll('.native-option-select').forEach(select => {
      select.addEventListener('change', () => {
        syncCards(root);
        scheduleCardSync();
      });
    });
    root.querySelector('[data-choice-support-help]')?.addEventListener('click', event => {
      emit('product_choice_support_help', root, {
        routing_key: event.currentTarget.dataset.choiceRoutingKey || '',
      });
    });

    syncDisclosureLayout(root);
    syncCards(root);
    unlockPurchaseForm(root);
    observeImpression(root);
  }

  function initialize() {
    document.querySelectorAll('[data-product-choice-support]').forEach(initializeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
  window.matchMedia?.('(max-width: 767px)').addEventListener?.('change', () => {
    document.querySelectorAll('[data-product-choice-support]').forEach(root => {
      syncDisclosureLayout(root);
      syncCards(root);
    });
  });
})();
